import { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { SectionGridRow, SectionGridRowProps } from '../../SectionGrid/SectionGridRow';
import { SectionGridItem, SectionGridItemProps } from '../../SectionGrid/SectionGridItem';
import {
  getSessionStatusTranslatedLabel,
  SESSION_STATUS_UI,
  SessionStatusUi,
} from '../../../../simInventory/utils/sessionStatus';
import { SessionStatusIndicator } from '../../../../simInventory/Components/SessionStatusIndicator';
import Typography from '@mui/material/Typography';
import { ContentCopyExtension } from '../../../ContentCopyExtension/ContentCopyExtension';
import { TagDisplay } from '../../../../tags/TagsDisplay';
import { Tag, TagsData } from '../../../../tags/models/tagsApi.types';
import { CopyableLink } from '../../Table/Link';
import { Routes } from '../../../../Routes.constants';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { SubscriptionStatusBadge } from 'src/common/components/ConfigurableTable/Components/Badges/SubscriptionStatusBadge';
import { OrderSection } from '../../Order/Details/OrderSection';
import { DeviceSection } from '../../Device/Details/DeviceSection';
import { simGridSizes } from 'src/rSimInventory/Details/RSimInventoryDetailsPage.style';
import {
  CLIENT_SECTIONS_PROPS,
  IDENTIFICATION_NUMBERS_SECTIONS_PROPS,
  MOBILE_NETWORK_OPERATOR_SECTIONS_PROPS,
  SESSION_DETAILS_SECTIONS_PROPS,
  SIM_PROFILES_SECTIONS_PROPS,
  USAGE_SECTIONS_PROPS,
} from './constants/sections';
import { SimData } from '../constants/sim';
import { DisplaySection } from './DisplaySection';
import {
  SUBSCRIPTION_STATUS,
  SubscriptionStatus,
} from '../../SubscriptionStatus/constants/subscriptionStatus';
import { isEmpty } from 'lodash';
import { DetailsType, UsageChartSection } from './UsageChartsSection';
import { SectionLoader } from './SectionLoader';
import { CustomFieldsDetails } from './CustomFieldsDetails';
import {
  SectionGridNumericItem,
  SectionGridNumericItemProps,
} from '../../SectionGrid/SectionGridNumericItem';
import {
  SectionGriDurationItem,
  SectionGriDurationItemProps,
} from '../../SectionGrid/SectionGriDurationItem';
import { NetworkDetails } from './NetworkDetails';
import { getNetworkDetailsData } from './networkDetails.utils';

const SessionStatusBox = styled(Box)(({ theme }) => ({
  paddingRight: theme.spacing(16),
}));

const SimDetailsRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'withHorizonalPadding',
})<{ withHorizonalPadding: boolean }>(({ theme, withHorizonalPadding }) => ({
  paddingLeft: withHorizonalPadding ? theme.spacing(64) : 0,
  paddingRight: withHorizonalPadding ? theme.spacing(64) : 0,
  paddingTop: theme.spacing(12),
  paddingBottom: theme.spacing(56),
}));

type SimDetailsProps = {
  data: SimData;
  rSimView?: boolean;
  rSimViewSimProfileRow?: ReactNode | null;
  baseRoute: string;
  isSessionDetailsLoading?: boolean;
};

const SimSectionGridRow = ({
  rSimView = false,
  ...props
}: SectionGridRowProps & { rSimView?: boolean }) => {
  return <SectionGridRow {...props} sx={{ pl: rSimView ? 0 : 12, ...props.sx }} />;
};

const SimSectionGridItem = ({
  rSimView = false,
  ...props
}: Omit<SectionGridItemProps, 'customGridSizes'> & { rSimView?: boolean }) => {
  return <SectionGridItem {...props} customGridSizes={rSimView ? simGridSizes : null} />;
};

const SimSectionGridItemNumeric = ({
  rSimView = false,
  ...props
}: Omit<SectionGridNumericItemProps, 'customGridSizes'> & { rSimView?: boolean }) => {
  return <SectionGridNumericItem {...props} customGridSizes={rSimView ? simGridSizes : null} />;
};

const SimSectionGriDurationItem = ({
  rSimView = false,
  ...props
}: Omit<SectionGriDurationItemProps, 'customGridSizes'> & { rSimView?: boolean }) => {
  return <SectionGriDurationItem {...props} customGridSizes={rSimView ? simGridSizes : null} />;
};

const SessionStatusDetail = ({ sessionStatus }: { sessionStatus?: SessionStatusUi }) => {
  return sessionStatus == SESSION_STATUS_UI.NONE ? null : (
    <SessionStatusBox>
      <SessionStatusIndicator status={sessionStatus} />
    </SessionStatusBox>
  );
};

const SubscriptionStatusDetail = ({
  subscriptionStatus,
}: {
  subscriptionStatus?: SubscriptionStatus | null;
}) => {
  return subscriptionStatus === undefined ? undefined : (
    <SubscriptionStatusBadge
      subscriptionStatus={subscriptionStatus ?? SUBSCRIPTION_STATUS.UNKNOWN}
    />
  );
};

const TagsGridItem = ({
  id,
  rSimView,
  tags,
}: {
  id: string;
  rSimView: boolean;
  tags?: TagsData;
}) => {
  const { t } = useTranslation();
  const gridSize = rSimView
    ? simGridSizes
    : {
        xsSize: 12,
        smSize: 12,
        mdSize: 12,
        lgSize: 12,
        xlSize: 12,
      };

  return (
    <SectionGridItem testId="tags" label={t('common.tags')} customGridSizes={gridSize}>
      {isEmpty(tags?.items) ? (
        <span>-</span>
      ) : (
        <TagDisplay
          id={id}
          initialTags={tags?.items.map((item: Tag) => item.name) || []}
          hasMore={tags?.hasMore || false}
          shouldWrap={true}
          preventRefetch
        />
      )}
    </SectionGridItem>
  );
};

export const SimDetails = ({
  data,
  rSimView = false,
  rSimViewSimProfileRow,
  baseRoute,
  isSessionDetailsLoading,
  ...props
}: SimDetailsProps) => {
  const { iccid } = data;
  const { t } = useTranslation();
  const { hasNetworkIdentifiers, homeNetwork, lastRoamingNetwork } = getNetworkDetailsData(data);

  return (
    <SimDetailsRoot withHorizonalPadding={!rSimView} {...props}>
      <Grid container direction="column">
        {rSimView ? null : (
          <DisplaySection data={data} keys={SIM_PROFILES_SECTIONS_PROPS}>
            <SimSectionGridRow hideTopBorder={true} rSimView title={t('simInventory.simProfile')}>
              <SectionGridItem
                label={rSimView ? t('simInventory.profileStatus') : t('simInventory.sessionStatus')}
                customGridSizes={rSimView ? simGridSizes : null}
                testId="sessionProfileStatus"
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SessionStatusDetail sessionStatus={data.sessionStatus} />
                  <Typography variant="text1" color="text.primary">
                    <ContentCopyExtension
                      value={getSessionStatusTranslatedLabel(data.sessionStatus) || ''}
                    >
                      {getSessionStatusTranslatedLabel(data.sessionStatus)}
                    </ContentCopyExtension>
                  </Typography>
                </Box>
              </SectionGridItem>

              <SimSectionGridItem
                rSimView
                value={data?.sessionStatusLastUpdated}
                label={t('simInventory.sessionStatusLastUpdated_flat')}
                testId="sessionStatusLastUpdatedSimProfile"
              />
              <SimSectionGridItem
                label={t('simInventory.subscriptionStatus')}
                testId="subscriptionStatus"
                rSimView
              >
                <SubscriptionStatusDetail subscriptionStatus={data?.subscriptionStatus} />
              </SimSectionGridItem>
              <SimSectionGridItem
                value={data?.simType}
                label={t('simInventory.simType')}
                testId="simType"
                rSimView
              />
              <SimSectionGridItem
                value={data?.productCode}
                label={t('simInventory.productCode')}
                testId="productCode"
                rSimView
              />
            </SimSectionGridRow>
            <SimSectionGridRow
              hideTopBorder={true}
              rSimView
              sx={{
                pb: 12,
              }}
            >
              <TagsGridItem tags={data?.tags} id={iccid} rSimView={rSimView} />
            </SimSectionGridRow>
          </DisplaySection>
        )}
        {rSimView ? rSimViewSimProfileRow : null}
        <DisplaySection data={data} keys={MOBILE_NETWORK_OPERATOR_SECTIONS_PROPS}>
          <SimSectionGridRow
            rSimView={rSimView}
            title={t('simInventory.mobileNetworkOperatorSectionLabel')}
          >
            <SimSectionGridItem
              testId={'mobileNetworkOperator'}
              value={data?.mobileNetworkOperator}
              label={t('simInventory.mobileNetworkOperator')}
              rSimView={rSimView}
            />
            <SimSectionGridItem
              testId={'commsPlan'}
              value={data?.commsPlan}
              label={t('simInventory.commsPlan')}
              rSimView={rSimView}
            />
            <SimSectionGridItem
              testId={'organisation'}
              value={data?.organisation}
              label={t('simInventory.enterprise')}
              rSimView={rSimView}
            />
            <SimSectionGridItem
              testId={'dcpEnterprise'}
              value={data?.dcpEnterprise}
              label={t('common.dcpEnterprise')}
              rSimView={rSimView}
            />
            <SimSectionGridItem
              testId={'mnoStatus'}
              value={data?.mnoStatus}
              label={t('simInventory.mnoStatus')}
              rSimView={rSimView}
            />

            <SimSectionGridItem
              testId={'mnoStatusEntryTime'}
              value={data?.mnoStatusEntryTime}
              label={
                rSimView ? t('simInventory.changeStatusTime') : t('simInventory.mnoStatusEntryTime')
              }
              rSimView={rSimView}
            />
            <SimSectionGridItem
              testId={'mnoRatePlan'}
              value={data?.mnoRatePlan}
              label="MNO Rate Plan"
              rSimView={rSimView}
            />
          </SimSectionGridRow>
        </DisplaySection>
        {!rSimView && (
          <DisplaySection data={data} keys={CLIENT_SECTIONS_PROPS}>
            <SimSectionGridRow title={t('simInventory.client')}>
              <SimSectionGridItem
                testId="accountName"
                value={data?.accountName}
                label={t('common.accountName')}
              />
              <SimSectionGridItem
                testId="accountRef"
                value={data?.accountRef}
                label={t('common.accountNumber')}
              />
              <SimSectionGridItem
                value={data?.installerName}
                label={t('simInventory.organisationName')}
                testId="installerName"
              />
              <SimSectionGridItem
                value={data?.installerRef?.toString()}
                label={t('simInventory.organisationNumber')}
                testId="installerRef"
              />
              <SimSectionGridItem
                testId="customerCommsPlan"
                value={data?.customerCommsPlan}
                label={t('simInventory.customerCommsPlan')}
              />
            </SimSectionGridRow>
          </DisplaySection>
        )}
        <DisplaySection data={data} keys={IDENTIFICATION_NUMBERS_SECTIONS_PROPS}>
          <SimSectionGridRow title={t('simInventory.identificationNumbers')} rSimView={rSimView}>
            {rSimView ? (
              <SimSectionGridItem rSimView label={t('common.iccid')} testId={`iccid`}>
                <CopyableLink value={iccid} to={`${baseRoute}/${iccid}`} />
              </SimSectionGridItem>
            ) : (
              <SimSectionGridItem testId={'iccid'} value={data?.iccid} label={t('common.iccid')} />
            )}
            <SimSectionGridItem
              testId={'imsi'}
              value={data?.imsi}
              label={t('common.imsi')}
              rSimView={rSimView}
            />
            <SimSectionGridItem
              testId={'msisdn'}
              value={data?.msisdn}
              label={t('common.msisdn')}
              rSimView={rSimView}
            />
            {rSimView ? null : (
              <SectionGridItem testId={'eid'} label={t('common.eid')}>
                {data?.euicc ? (
                  <CopyableLink value={data?.euicc} to={`${Routes.productsRsim}/${data?.euicc}`} />
                ) : (
                  <>-</>
                )}
              </SectionGridItem>
            )}
          </SimSectionGridRow>
        </DisplaySection>
        {!rSimView && <DeviceSection deviceData={data} isLoading={isSessionDetailsLoading} />}
        {!rSimView && <OrderSection orderData={data} />}
        <DisplaySection data={data} keys={USAGE_SECTIONS_PROPS}>
          <SimSectionGridRow
            title={
              rSimView ? t('simInventory.profileUsageInCycle') : t('simInventory.usageInCycle')
            }
            rSimView={rSimView}
          >
            <SimSectionGridItemNumeric
              value={data?.liveDataUsage}
              label={t('products.sim.dataMB')}
              testId={'dataUsage'}
              rSimView={rSimView}
            />
            <SimSectionGridItemNumeric
              value={data?.inboundLiveDataUsage}
              label={t('products.sim.dataMT')}
              testId="inDataUsage"
              rSimView={rSimView}
            />
            <SimSectionGridItemNumeric
              value={data?.outboundLiveDataUsage}
              label={t('products.sim.dataMO')}
              testId="outDataUsage"
              rSimView={rSimView}
            />
            <SimSectionGriDurationItem
              value={data?.inMinuteUsage}
              label={t('products.sim.voiceMT')}
              testId="inVoiceUsage"
              rSimView={rSimView}
            />
            <SimSectionGriDurationItem
              value={data?.outMinuteUsage}
              label={t('products.sim.voiceMO')}
              testId="outVoiceUsage"
              rSimView={rSimView}
            />
            <SimSectionGridItemNumeric
              value={data?.inTextUsage}
              label={t('products.sim.smsMT')}
              testId="inSmsUsage"
              rSimView={rSimView}
            />
            <SimSectionGridItemNumeric
              value={data?.outTextUsage}
              label={t('products.sim.smsMO')}
              testId="outSmsUsage"
              rSimView={rSimView}
            />
            <SimSectionGridItemNumeric
              value={data?.outTextUsage}
              label={t('products.sim.smsMO')}
              testId="outSmsUsage"
              rSimView={rSimView}
            />
          </SimSectionGridRow>
          {!rSimView && <UsageChartSection detailsType={DetailsType.Sim} fetchBy={iccid} />}
        </DisplaySection>
        {!rSimView && (
          <SectionLoader
            isLoading={isSessionDetailsLoading}
            data-testid="last-session-details-section-skeleton"
          >
            <DisplaySection data={data} keys={SESSION_DETAILS_SECTIONS_PROPS}>
              <SimSectionGridRow title={t('simInventory.lastSessionDetails')}>
                <SectionGridItem
                  value={getSessionStatusTranslatedLabel(data?.sessionStatus)}
                  label={t('simInventory.sessionStatus')}
                  testId="sessionStatus"
                />
                <SectionGridItem
                  value={data?.sessionStatusLastUpdated}
                  label={t('simInventory.sessionStatusLastUpdated_flat')}
                  testId="sessionStatusLastUpdatedSessionDetails"
                />
                <SectionGridItem
                  value={data?.sessionStartTime}
                  label={t('simInventory.sessionStartTime')}
                  testId="sessionStartTime"
                />
                <SectionGridItem
                  value={data?.sessionEndTime}
                  label={t('simInventory.sessionEndTime')}
                  testId="sessionEndTime"
                />
                <SectionGridItem
                  value={data?.cumulativeTotalUsage}
                  label={t('simInventory.cumulativeUsage')}
                  testId="cumulativeUsage"
                />
                <SectionGridItem
                  value={data?.apn}
                  label={t('simInventory.apn')}
                  testId="apnLastSessionDetails"
                />
                <SectionGridItem
                  value={data?.localization}
                  label={t('simInventory.location')}
                  testId="location"
                />
                <SectionGridItem
                  value={data?.ratType}
                  label={t('simInventory.lastRat')}
                  testId="ratType"
                />
                <SectionGridItem
                  value={data?.rasClient}
                  label={t('simInventory.rasClient')}
                  testId="rasClient"
                />
                <SectionGridItem
                  value={data?.framedProtocol}
                  label={t('simInventory.framedProtocol')}
                  testId="framedProtocol"
                />
                <SectionGridItem
                  value={data?.nasIpAddress}
                  label={t('simInventory.nasIpAddress')}
                  testId="nasIpAddress"
                />
                <SectionGridItem
                  value={data?.serviceType}
                  label={t('simInventory.serviceType')}
                  testId="serviceType"
                />
                <SectionGridItem
                  value={data?.userName}
                  label={t('simInventory.userName')}
                  testId="userName"
                />
              </SimSectionGridRow>
              {hasNetworkIdentifiers ? (
                <SimSectionGridRow title={t('simInventory.networkIdentifiers')} data-testid="networkIdentifiers">
                  <Grid size={12}>
                    <NetworkDetails
                      homeNetwork={homeNetwork}
                      lastRoamingNetwork={lastRoamingNetwork}
                      testIdPrefix="networkIdentifiers"
                    />
                  </Grid>
                </SimSectionGridRow>
              ) : null}
            </DisplaySection>
          </SectionLoader>
        )}
        <CustomFieldsDetails rSimView={rSimView} customFields={data?.customFields} />
      </Grid>
    </SimDetailsRoot>
  );
};
